import { trackEvent } from 'helpers/analytics'
import { isNumber } from 'lodash'
import {
  MATCHING_PROFILE_INTRO_STEPS_CONFIG,
  MATCHING_PROFILE_PART_THREE_STEPS_CONFIG,
  MATCHING_PROFILE_PART_TWO_STEPS_CONFIG
} from '../components/joyride/MatchingProfileJoyride'

export const EVALUATION_TYPES = {
  MATCHINGPROFILE: 'MATCHINGPROFILE',
  REQUIREMENTPROFILE: 'REQUIREMENTPROFILE',
  TALENTPROFILE: 'TALENTPROFILE',
  TEAMPROFILE: 'TEAMPROFILE',
  CROSSCOMPARISON: 'CROSSCOMPARISON',
  CAREERSMATCHING: 'CAREERSMATCHING'
}

export const extractTalentName = (talent, t) =>
  talent?.person
    ? `${talent.person.firstname || talent.person.invitation_firstname} ${talent.person.lastname || talent.person.invitation_lastname}`
    : t('common.unknown_talent')

export function getStepState({ active, completed }) {
  if (completed) return 'completed'
  if (active) return 'active'
  return 'upcoming'
}

export const getVariantStyles = (variant) => {
  switch (variant) {
    case 'success':
      return 'bg-lightGreen text-darkGreen'
    case 'warning':
      return 'bg-lightYellow text-darkYellow'
    case 'danger':
      return 'bg-lightRed text-darkRed'
    default:
      return 'bg-blue-50 text-blue-700' // default variant
  }
}

/**
 * Used for demo career discover
 */
export const DEFAULT_CULTURAL_FIT_STATE = {
  HEROS_JOURNEY_score_coopcomp: 0,
  HEROS_JOURNEY_score_orderchaos: 0
}

export const DEFAULT_CHALLENGES = [
  {
    exam_id: 'RIASEC_IMAGES'
  },
  {
    exam_id: 'PATH_FINDER'
  },
  {
    exam_id: 'NBACK'
  },
  {
    exam_id: 'DIGIT_SPAN'
  },
  {
    exam_id: 'BIG_FIVE'
  },
  {
    exam_id: 'HEROS_JOURNEY'
  },
  {
    exam_id: 'FLANKER'
  },
  {
    exam_id: 'TOWER_OF_LONDON'
  },
  {
    exam_id: 'ANALOGICAL_REASONING'
  },
  {
    exam_id: 'NUMERACY'
  },
  {
    exam_id: 'EMOTIONS_GAME'
  },
  {
    exam_id: 'DELEGATION'
  }
]

export const DEFAULT_DIMENSIONS = [
  'BIG_FIVE_n_score',
  'BIG_FIVE_a_score',
  'BIG_FIVE_c_score',
  'BIG_FIVE_o_score',
  'BIG_FIVE_e_score',
  'HEROS_JOURNEY_score_coopcomp',
  'HEROS_JOURNEY_score_orderchaos',
  'PROBLEM_SOLVING_score',
  'ANALOGICAL_REASONING_score',
  'NUMERACY_score',
  'EMOTIONS_GAME_score',
  'DELEGATION_score_risk',
  'DELEGATION_score_responsibility'
]

export const EVALUATION_CONTEXT = {
  MATCHINGPROFILE: 'MATCHINGPROFILE',
  REQUIREMENTPROFILE: 'REQUIREMENTPROFILE',
  TALENTPROFILE: 'TALENTPROFILE',
  TEAMPROFILE: 'TEAMPROFILE',
  CROSSCOMPARISON: 'CROSSCOMPARISON',
  CAREERSMATCHING: 'CAREERSMATCHING'
}

export const MESSAGE_TYPES = {
  TEXT: 'text',
  CTA: 'cta',
  OPTION: 'option',
  INVITE_DEMO_TALENT: 'invite_demo_talent',
  DIMENSION_CAREER_TALENT: 'dimension_career_talent',
  DIMENSION_PREVIEW: 'dimension_preview',
  NEXT_STEPS_PROMPT: 'next_steps_prompt',
  TALENT_MATCH_SUGGESTIONS: 'talent_match_suggestions',
  COMPANY_PROFILE_ADOPTION: 'company_profile_adoption',
  NAME_INPUT: 'name_input',
  CULTURAL_FIT_CAREER_TALENT: 'cultural_fit_career_talent',
  RIASEC_FIT_CAREER_TALENT: 'riasec_fit_career_talent',
  CATEGORY_SELECTION: 'category_selection',
  SKILLS_DETAILS: 'skills_details',
  CULTURE_FIT_DETAILS: 'culture_fit_details',
  PERSONALITY_DETAILS: 'personality_details',
  MATCHING_INSIGHTS: 'matching_insights',
  INTERESTS_DETAILS: 'interests_details',
  VIDEO: 'video',
  FEATURE: 'feature'
}

export const MESSAGE_ROLES = {
  USER: 'user',
  ASSISTANT: 'assistant'
}

export const sendSmartMessageSequence = async ({
  messages,
  baseDelayPerChar = 20,
  initialDelay = 1500,
  setIsTyping = () => {},
  callbackFn = () => {}
}) => {
  return new Promise((resolve) => {
    let totalDelay = initialDelay

    messages.forEach(({ fn, textLength = 0 }, index) => {
      const typeMultiplier = 1
      const minDelay = 1000
      const maxDelay = 5000

      const estimatedDelay = Math.min(
        Math.max(textLength * baseDelayPerChar * typeMultiplier, minDelay),
        maxDelay
      )

      if (index < messages.length - 1) {
        setTimeout(() => setIsTyping(true), totalDelay)
      }

      setTimeout(() => {
        fn()

        if (index === messages.length - 1) {
          setIsTyping(false)
          callbackFn()
          resolve()
        }
      }, totalDelay)

      totalDelay += estimatedDelay
    })
  })
}

export const startMatchingProfileIntroAssistantFlow = ({
  sendMessageToAssistant,
  setAssistantIsTyping,
  setEnableClickOnFirstDimension,
  setAssistantSpotlightSelector,
  t
}) => {
  const steps = MATCHING_PROFILE_INTRO_STEPS_CONFIG

  const sendStep = (stepIndex) => {
    const step = steps[stepIndex]
    if (!step) {
      setAssistantSpotlightSelector(null)
      return
    }

    setAssistantSpotlightSelector(step.target)

    const isLast = stepIndex === steps.length - 1

    if (typeof setAssistantIsTyping === 'function') {
      setAssistantIsTyping(false)
    }

    sendMessageToAssistant({
      id: step.id,
      title: t(step.title),
      description: t(step.description),
      role: MESSAGE_ROLES.ASSISTANT,
      type: MESSAGE_TYPES.TEXT,
      spotlightSelector: step.target,
      cta: !isLast
        ? {
            title: t(step.ctaTitle),
            onClick: () => {
              if (step.ctaTitle === 'discover.assistant_onboarding.9.cta') {
                trackEvent(
                  'discover_matching_profile_overall_matching_score_completed'
                )
              } else if (
                step.ctaTitle === 'discover.assistant_onboarding.10.cta'
              ) {
                trackEvent(
                  'discover_matching_profile_dimension_match_breakdown_completed'
                )
              }
              sendStep(stepIndex + 1)
            },
            isPulsing: step.isCtaPulsing || false
          }
        : {
            title: t(step.ctaTitle),
            onClick: () => {
              if (typeof setEnableClickOnFirstDimension === 'function') {
                trackEvent(
                  'discover_matching_profile_problem_solving_dimension_clicked'
                )
                setEnableClickOnFirstDimension(true)
              }
            },
            isPulsing: step.isCtaPulsing || false
          }
    })
  }

  sendStep(0)
}

export const startMatchingProfilePartTwoAssistantFlow = ({
  sendMessageToAssistant,
  setAssistantIsTyping,
  setEnableClickOnFirstMiddleFitDimension,
  setAssistantSpotlightSelector,
  t
}) => {
  const [step] = MATCHING_PROFILE_PART_TWO_STEPS_CONFIG
  if (!step) return

  if (typeof setAssistantIsTyping === 'function') {
    setAssistantIsTyping(false)
  }

  if (typeof setAssistantSpotlightSelector === 'function') {
    setAssistantSpotlightSelector(step.target)
  }

  sendMessageToAssistant({
    id: step.id,
    title: t(step.title),
    description: t(step.description),
    role: MESSAGE_ROLES.ASSISTANT,
    type: MESSAGE_TYPES.TEXT,
    spotlightSelector: step.target,
    cta: {
      title: t('discover.assistant_onboarding.12.cta'),
      onClick: () => {
        trackEvent('discover_matching_profile_match_dimension_clicked')
        if (typeof setAssistantSpotlightSelector === 'function') {
          setAssistantSpotlightSelector(null)
        }
        if (typeof setEnableClickOnFirstMiddleFitDimension === 'function') {
          setEnableClickOnFirstMiddleFitDimension(true)
        }
      }
    }
  })
}

export const startMatchingProfilePartThreeAssistantFlow = ({
  sendMessageToAssistant,
  setAssistantIsTyping,
  setCompletionSummaryModalOpen,
  setShowFeedbackModal,
  setAssistantSpotlightSelector,
  clearAssistantMessages,
  t,
  hideAssistant
}) => {
  const [step] = MATCHING_PROFILE_PART_THREE_STEPS_CONFIG
  if (!step) return

  if (typeof setAssistantIsTyping === 'function') {
    setAssistantIsTyping(false)
  }

  if (typeof setAssistantSpotlightSelector === 'function') {
    setAssistantSpotlightSelector(step.target)
  }

  sendMessageToAssistant({
    id: step.id,
    title: t(step.title),
    description: t(step.description),
    role: MESSAGE_ROLES.ASSISTANT,
    type: MESSAGE_TYPES.TEXT,
    spotlightSelector: step.target,
    cta: {
      title: t('discover.assistant_onboarding.13.cta'),
      onClick: () => {
        trackEvent('discover_matching_profile_chances_risks_continued')
        if (typeof setAssistantSpotlightSelector === 'function') {
          setAssistantSpotlightSelector(null)
        }
        clearAssistantMessages()
        hideAssistant()
        setShowFeedbackModal(true)
      }
    }
  })
}

export const getGreetingKeyByTime = () => {
  const hour = new Date().getHours()
  if (hour >= 5 && hour < 11) return '1'
  if (hour >= 11 && hour < 18) return '2'
  return '3'
}

export const getVariantFromScoreKey = (score_key) => {
  if (!score_key) return null
  switch (score_key) {
    case 'green':
    case 'score>50':
      return 'success'
    case 'yellow&score>50':
    case 'yellow&score<=50':
      return 'warning'
    case 'red':
    case 'score<=50':
      return 'danger'
    default:
      return null
  }
}

export const getOverallKey = (matching) => {
  const { matching_score_to_career: score } = matching

  const mandatory_dimensions = matching.dimension_matches.filter(
    ({ mandatory_criterion }) => mandatory_criterion === true
  )

  if (!mandatory_dimensions.length) {
    if (score > 50) return 'score>50'
    else if (score <= 50) return 'score<=50'
  }

  // be aware of the order!

  if (
    mandatory_dimensions.some(({ dimension_match }) => dimension_match === 0)
  ) {
    return 'red'
  }

  if (
    mandatory_dimensions.some(({ dimension_match }) => dimension_match === 0.5)
  ) {
    if (score > 50) return 'yellow&score>50'
    else if (score <= 50) return 'yellow&score<=50'
  }

  if (
    mandatory_dimensions.filter(({ dimension_match }) => dimension_match === 1)
      .length === mandatory_dimensions.length
  ) {
    return 'green'
  }

  throw new Error(`overall not found / space_id: ${matching.user_id}`)
}

const high_fit = {
  dark: '#0F6856',
  medium: '#4FF0D0',
  light: '#C7FFF4'
}

const medium_fit = {
  dark: '#774E00',
  medium: '#FFB629',
  light: '#FFE7B9'
}

const low_fit = {
  dark: '#9D174D',
  medium: '#FE78A7',
  light: '#FCE7F3'
}

export const getColors = ({ score, score_key, fit }) => {
  // be aware of the order
  if (score_key) {
    return {
      green: high_fit,
      'score>50': high_fit,
      'yellow&score>50': medium_fit,
      'yellow&score<=50': medium_fit,
      red: low_fit,
      'score<=50': low_fit
    }[score_key]
  }

  if (isNumber(score)) {
    if (score >= 70) return high_fit
    else if (score > 30 && score < 70) return medium_fit
    // score <= 30
    else return low_fit
  }

  if (isNumber(fit)) {
    // dimension_match is continuous (0 to 1) for RIASEC from scoring revision 2, else 0 / 0.5 / 1.
    // Same bands as the backend (MATCHINGPROFILE): >= 0.7 high, > 0.3 medium, else low.
    if (fit >= 0.7) return high_fit
    else if (fit > 0.3) return medium_fit
    else return low_fit
  }
}

export const CULTURAL_FIT_DIMENSION_NAMES = [
  'Unternehmenskultur',
  'Company Culture'
]

export const RIASEC_FIT_DIMENSION_NAMES = [
  'Berufliche Interessen',
  'Professional interests'
]

export const showDemoAlert = () => {
  alert('Diese Funktion ist in der Demo nicht verfügbar')
}

export const encodeSelection = (talents = [], careers = []) => {
  const selection = {
    t: talents, // talents
    c: careers // careers
  }
  return btoa(JSON.stringify(selection))
}

export const decodeSelection = (encodedSelection) => {
  try {
    if (!encodedSelection) return { talents: [], careers: [] }
    const decoded = JSON.parse(atob(encodedSelection))

    return {
      talents: decoded.t || [],
      careers: decoded.c || []
    }
  } catch (error) {
    console.error('Error decoding selection:', error)
    return { talents: [], careers: [] }
  }
}

export const updateSelectionUrl = (
  talents,
  careers,
  navigate,
  location,
  state = {}
) => {
  const encoded = encodeSelection(talents, careers)
  const searchParams = new URLSearchParams(location.search)

  if (talents.length > 0 || careers.length > 0) {
    searchParams.set('selection', encoded)
  } else {
    searchParams.delete('selection')
  }

  navigate(`/discover/?${searchParams.toString()}`, {
    replace: true,
    state
  })
}

// Standardprofil zur Auswahl aus der URL. Liefert null fuer Kombinationen
// ohne eindeutiges Profil (z. B. nichts ausgewaehlt), dann bleibt die
// bisherige Auswahl stehen.
export const getDefaultResult = (talentIds, careerIds) => {
  if (talentIds.length === 1 && careerIds.length === 0) {
    return EVALUATION_CONTEXT.TALENTPROFILE
  }
  if (talentIds.length === 0 && careerIds.length === 1) {
    return EVALUATION_CONTEXT.REQUIREMENTPROFILE
  }
  if (talentIds.length === 1 && careerIds.length === 1) {
    return EVALUATION_CONTEXT.MATCHINGPROFILE
  }
  if (talentIds.length > 1) return EVALUATION_CONTEXT.TEAMPROFILE
  return null
}

export const getProfileOptions = (t) => [
  {
    key: 'MATCHINGPROFILE',
    label: t('discover.profile_options.MATCHINGPROFILE')
  },
  {
    key: 'REQUIREMENTPROFILE',
    label: t('discover.profile_options.REQUIREMENTPROFILE')
  },
  {
    key: 'TALENTPROFILE',
    label: t('discover.profile_options.TALENTPROFILE')
  },
  {
    key: 'TEAMPROFILE',
    label: t('discover.profile_options.TEAMPROFILE')
  }
]

export const createDefaultGoBackSequenceMessage = ({
  t,
  hideAssistant,
  sendMessageToAssistant,
  setAssistantIsTyping,
  id = 4,
  shouldClickOnMandatoryDimension,
  setShouldClickOnMandatoryDimension,
  setShouldShowCompletionOnboardingModal,
  enableClickOnFirstDimension = false,
  enableClickOnFirstMiddleFitDimension = false,
  setEnableClickOnFirstMiddleFitDimension,
  setCompletionSummaryModalOpen,
  setShowFeedbackModal,
  setAssistantSpotlightSelector,
  clearAssistantMessages
}) => ({
  fn: () =>
    sendMessageToAssistant({
      id,
      role: MESSAGE_ROLES.ASSISTANT,
      type: MESSAGE_TYPES.TEXT,
      description: t('discover.assistant.default_go_back.description'),
      cta: {
        title: t('discover.assistant.default_go_back.cta'),
        onClick: () => {
          hideAssistant()
          const localOnboardingCompleted =
            localStorage.getItem('showed_completion_onboarding_modal') ===
            'true'

          if (enableClickOnFirstMiddleFitDimension) {
            startMatchingProfilePartThreeAssistantFlow({
              sendMessageToAssistant,
              setAssistantIsTyping,
              setCompletionSummaryModalOpen,
              setShowFeedbackModal,
              clearAssistantMessages,
              t,
              hideAssistant
            })
            return
          }

          if (enableClickOnFirstDimension) {
            startMatchingProfilePartTwoAssistantFlow({
              sendMessageToAssistant,
              setAssistantIsTyping,
              setEnableClickOnFirstMiddleFitDimension,
              setAssistantSpotlightSelector,
              t
            })
            return
          }

          if (
            shouldClickOnMandatoryDimension === true &&
            localOnboardingCompleted === false
          ) {
            setTimeout(() => {
              setShouldClickOnMandatoryDimension(false)
              setShouldShowCompletionOnboardingModal(true)
              localStorage.setItem('showed_completion_onboarding_modal', 'true')
            }, 1500)
          }
        }
      }
    }),
  textLength: t('discover.assistant.default_go_back.description').length
})

export const ONBOARDING_MESSAGES = [
  {
    id: 'welcome',
    title: 'discover.assistant_onboarding.2.title',
    descriptionMarkdownDe: 'discover.assistant_onboarding.2.description'
  }
]

export const spotlights = {
  requirementProfile: false
}
